import {
  type ReactNode,
  type RefObject,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';
import { s8, s16 } from '@ardoq/design-tokens';
import type { ToastPhase } from './types';
import { slideEnter, slideExit, slotEnter, slotExit } from './animationUtils';

type ExitSnapshot = { top: number; height: number; width: number };

type SlotProps = { $exitSnapshot: ExitSnapshot | null };

const toExitSnapshotVars = (snapshot: ExitSnapshot | null) =>
  snapshot
    ? {
        '--toast-slot-top': `${snapshot.top}px`,
        '--toast-slot-height': `${snapshot.height}px`,
        '--toast-slide-width': `${snapshot.width}px`,
      }
    : undefined;

const ToastSlot = styled.div.attrs<SlotProps>(({ $exitSnapshot }) => ({
  style: toExitSnapshotVars($exitSnapshot),
}))<SlotProps>`
  position: relative;
  display: grid;
  justify-items: end;
  ${({ $exitSnapshot }) => ($exitSnapshot ? slotExit : slotEnter)}
`;

const ToastSlotCollapse = styled.div`
  min-height: 0;
`;

const ToastSlide = styled.div<SlotProps>`
  padding: ${s8} ${s16};
  ${({ $exitSnapshot }) => ($exitSnapshot ? slideExit : slideEnter)}
`;

const toExitSnapshot = (
  slot: HTMLElement,
  slide: HTMLElement
): ExitSnapshot => ({
  top: slot.offsetTop,
  height: slot.offsetHeight,
  width: slide.getBoundingClientRect().width,
});

const useExitSnapshot = (
  slotRef: RefObject<HTMLDivElement | null>,
  slideRef: RefObject<HTMLDivElement | null>,
  phase: ToastPhase
): ExitSnapshot | null => {
  const [snapshot, setSnapshot] = useState<ExitSnapshot | null>(null);

  useLayoutEffect(() => {
    if (phase === 'open' || !slotRef.current || !slideRef.current) return;
    setSnapshot(toExitSnapshot(slotRef.current, slideRef.current));
  }, [phase, slotRef, slideRef]);

  return snapshot;
};

type AnimatedToastProps = {
  phase: ToastPhase;
  children: ReactNode;
};

export const AnimatedToast = ({ phase, children }: AnimatedToastProps) => {
  const slotRef = useRef<HTMLDivElement>(null);
  const slideRef = useRef<HTMLDivElement>(null);
  const exitSnapshot = useExitSnapshot(slotRef, slideRef, phase);

  return (
    <ToastSlot
      ref={slotRef}
      $exitSnapshot={exitSnapshot}
      className="ardoq-toast-notification"
      inert={phase === 'closing'}
    >
      <ToastSlotCollapse>
        <ToastSlide ref={slideRef} $exitSnapshot={exitSnapshot}>
          {children}
        </ToastSlide>
      </ToastSlotCollapse>
    </ToastSlot>
  );
};
